import {
  Document, Packer, Paragraph, TextRun, AlignmentType,
  Table, TableRow, TableCell, WidthType, ShadingType, VerticalAlign, BorderStyle,
} from 'docx';
import { formatIntakeValue } from './hocchokenIntakeFields';

const BODY_FONT = { ascii: 'MS Mincho', eastAsia: 'MS Mincho', hAnsi: 'MS Mincho' };

const THIN = { style: BorderStyle.SINGLE, size: 4, color: '000000' };
const NONE = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
const TABLE_BORDERS = { top: THIN, bottom: THIN, left: THIN, right: THIN, insideHorizontal: THIN, insideVertical: THIN };
const NO_BORDERS = { top: NONE, bottom: NONE, left: NONE, right: NONE, insideHorizontal: NONE, insideVertical: NONE };
const CELL_MARGINS = { top: 60, bottom: 60, left: 100, right: 100 };

function formatEntryDate(dateStr) {
  const [, m, d] = dateStr.split('-').map(Number);
  return `${m}/${d}`;
}

/** 様式番号（左）と法人名（右）を1行に並べたヘッダー（罫線なしの表で左右寄せを実現）。 */
function formHeaderLine(formNumber) {
  return new Table({
    width: { size: 9100, type: WidthType.DXA },
    columnWidths: [4550, 4550],
    borders: NO_BORDERS,
    rows: [
      new TableRow({
        children: [
          new TableCell({
            width: { size: 4550, type: WidthType.DXA },
            children: [new Paragraph({ children: [new TextRun({ text: formNumber, size: 18 })] })],
          }),
          new TableCell({
            width: { size: 4550, type: WidthType.DXA },
            children: [new Paragraph({ alignment: AlignmentType.RIGHT, children: [new TextRun({ text: '（社会福祉法人　ザ・ハート・クラブ）', size: 18 })] })],
          }),
        ],
      }),
    ],
  });
}

/** 見出しセル（網掛け・太字）。columnSpan/rowSpanで結合できる。 */
function labelCell(text, width, opts = {}) {
  return new TableCell({
    width: { size: width, type: WidthType.DXA },
    columnSpan: opts.columnSpan,
    rowSpan: opts.rowSpan,
    margins: CELL_MARGINS,
    shading: { type: ShadingType.CLEAR, fill: 'F2F2F2' },
    verticalAlign: VerticalAlign.CENTER,
    children: [new Paragraph({ children: [new TextRun({ text, bold: true })] })],
  });
}
/** 値セル。配列を渡すと複数行になる。空文字列はそのまま空欄で出す。 */
function valueCell(lines, width, opts = {}) {
  const arr = (Array.isArray(lines) ? lines : [lines]).filter((l) => l);
  return new TableCell({
    width: { size: width, type: WidthType.DXA },
    columnSpan: opts.columnSpan,
    rowSpan: opts.rowSpan,
    margins: CELL_MARGINS,
    verticalAlign: VerticalAlign.CENTER,
    children: arr.length ? arr.map((t) => new Paragraph({ children: [new TextRun(t)] }))
      : [new Paragraph({ children: [new TextRun('')] })],
  });
}

function otherLine(codeValue, key, otherText) {
  const label = formatIntakeValue(key, codeValue);
  return label === 'その他' && otherText ? `その他（${otherText}）` : label;
}

const RECEPTION_WIDTHS = [1800, 900, 3600, 2800];
function receptionTable(intake) {
  const dateLabel = intake.receptionDate ? formatIntakeValue('receptionDate', intake.receptionDate) : '';
  const timeLine = (intake.receptionTimeFrom || intake.receptionTimeTo)
    ? `${intake.receptionTimeFrom || ''}～${intake.receptionTimeTo || ''}` : '';
  const methodLine = otherLine(intake.receptionMethod, 'receptionMethod', intake.receptionMethodOther);

  return new Table({
    width: { size: 9100, type: WidthType.DXA },
    columnWidths: RECEPTION_WIDTHS,
    borders: TABLE_BORDERS,
    rows: [
      new TableRow({
        children: [
          labelCell('受付年月日', RECEPTION_WIDTHS[0]),
          labelCell('受付番号', RECEPTION_WIDTHS[1]),
          labelCell('困りごと相談受付形態', RECEPTION_WIDTHS[2]),
          labelCell('記入者', RECEPTION_WIDTHS[3]),
        ],
      }),
      new TableRow({
        children: [
          valueCell([dateLabel, timeLine], RECEPTION_WIDTHS[0]),
          valueCell(intake.receptionNumber, RECEPTION_WIDTHS[1]),
          valueCell(methodLine, RECEPTION_WIDTHS[2]),
          valueCell(intake.recorderName, RECEPTION_WIDTHS[3]),
        ],
      }),
    ],
  });
}

const PERSON_WIDTHS = [1200, 3400, 1200, 3300];
function pairRow(l1, v1, l2, v2) {
  return new TableRow({
    children: [
      labelCell(l1, PERSON_WIDTHS[0]),
      valueCell(v1, PERSON_WIDTHS[1]),
      labelCell(l2, PERSON_WIDTHS[2]),
      valueCell(v2, PERSON_WIDTHS[3]),
    ],
  });
}
function wideRow(l1, v1) {
  return new TableRow({
    children: [
      labelCell(l1, PERSON_WIDTHS[0]),
      valueCell(v1, PERSON_WIDTHS[1] + PERSON_WIDTHS[2] + PERSON_WIDTHS[3], { columnSpan: 3 }),
    ],
  });
}

function personTable(intake) {
  const birthLine = [formatIntakeValue('personBirthEra', intake.personBirthEra), intake.personBirthDate].filter(Boolean).join(' ');
  const relLine = otherLine(intake.relationshipType, 'relationshipType', intake.relationshipOther);
  return new Table({
    width: { size: 9100, type: WidthType.DXA },
    columnWidths: PERSON_WIDTHS,
    borders: TABLE_BORDERS,
    rows: [
      pairRow('フリガナ', intake.personFurigana, '電話番号', intake.personPhone),
      wideRow('氏　名', intake.personName),
      wideRow('連絡先', intake.personPostalCode ? `〒${intake.personPostalCode}` : ''),
      wideRow('住　所', intake.personAddress),
      pairRow('生年月日', birthLine, '年　齢', intake.personAge ? `${intake.personAge}歳` : ''),
      pairRow('性　別', formatIntakeValue('personGender', intake.personGender), '要支援度', intake.careSupportLevel),
      wideRow('要介護度', intake.careLevel),
      wideRow('当事者との関係', relLine),
    ],
  });
}

function reporterTable(intake) {
  return new Table({
    width: { size: 9100, type: WidthType.DXA },
    columnWidths: PERSON_WIDTHS,
    borders: TABLE_BORDERS,
    rows: [
      pairRow('フリガナ', intake.reporterFurigana, '続　柄', intake.reporterRelationship),
      pairRow('氏　名', intake.reporterName, '電話番号', intake.reporterPhone),
      wideRow('連絡先', intake.reporterPostalCode ? `〒${intake.reporterPostalCode}` : ''),
      wideRow('住　所', intake.reporterAddress),
    ],
  });
}

const ISSUE_WIDTHS = [1600, 1500, 6000];
function issueTable(intake) {
  const catLine = otherLine(intake.issueCategory, 'issueCategory', intake.issueCategoryOther);
  const reportLines = [formatIntakeValue('reportToOrgsNeeded', intake.reportToOrgsNeeded), intake.reportToOrgsNote ? `確認欄：${intake.reportToOrgsNote}` : '']
    .filter(Boolean);
  const attendLines = [formatIntakeValue('attendanceNeeded', intake.attendanceNeeded), intake.attendanceNote ? `確認欄：${intake.attendanceNote}` : '']
    .filter(Boolean);
  const issueContentLines = (intake.issueContent || '').split('\n').map((l) => l.trim()).filter(Boolean);
  const requestLines = (intake.reporterRequest || '').split('\n').map((l) => l.trim()).filter(Boolean);

  return new Table({
    width: { size: 9100, type: WidthType.DXA },
    columnWidths: ISSUE_WIDTHS,
    borders: TABLE_BORDERS,
    rows: [
      new TableRow({
        children: [
          labelCell('困りごとに係る事実のあった日', ISSUE_WIDTHS[0] + ISSUE_WIDTHS[1], { columnSpan: 2 }),
          valueCell(intake.issueOccurredDate ? formatIntakeValue('issueOccurredDate', intake.issueOccurredDate) : '', ISSUE_WIDTHS[2]),
        ],
      }),
      new TableRow({
        children: [
          labelCell('困りごとのある事業の種類', ISSUE_WIDTHS[0] + ISSUE_WIDTHS[1], { columnSpan: 2 }),
          valueCell(intake.issueServiceType, ISSUE_WIDTHS[2]),
        ],
      }),
      new TableRow({
        children: [
          labelCell('困りごとの内容', ISSUE_WIDTHS[0], { rowSpan: 2 }),
          labelCell('相談の分類', ISSUE_WIDTHS[1]),
          valueCell(catLine, ISSUE_WIDTHS[2]),
        ],
      }),
      new TableRow({
        children: [
          valueCell(issueContentLines, ISSUE_WIDTHS[1] + ISSUE_WIDTHS[2], { columnSpan: 2 }),
        ],
      }),
      new TableRow({
        children: [
          labelCell('申出人の要望', ISSUE_WIDTHS[0] + ISSUE_WIDTHS[1], { columnSpan: 2 }),
          valueCell(requestLines, ISSUE_WIDTHS[2]),
        ],
      }),
      new TableRow({
        children: [
          labelCell('申出人への確認', ISSUE_WIDTHS[0], { rowSpan: 2 }),
          labelCell('関係機関等への報告の要否', ISSUE_WIDTHS[1]),
          valueCell(reportLines, ISSUE_WIDTHS[2]),
        ],
      }),
      new TableRow({
        children: [
          labelCell('話し合いの際の関係機関等の立会いの要否', ISSUE_WIDTHS[1]),
          valueCell(attendLines, ISSUE_WIDTHS[2]),
        ],
      }),
    ],
  });
}

/** 対応経過／軽減・解決策／結果のような、罫線で囲った1つの記入欄を作る（様式１－②のイメージ）。 */
function boxedSection(label, paragraphs) {
  return new Table({
    width: { size: 9100, type: WidthType.DXA },
    columnWidths: [9100],
    borders: TABLE_BORDERS,
    rows: [
      new TableRow({
        children: [
          new TableCell({
            width: { size: 9100, type: WidthType.DXA },
            margins: { top: 120, bottom: 160, left: 140, right: 140 },
            children: [
              new Paragraph({ spacing: { after: 120 }, children: [new TextRun({ text: label, bold: true })] }),
              ...paragraphs,
            ],
          }),
        ],
      }),
    ],
  });
}

/** 「結果」が手入力されていない場合のフォールバック：対応経過をそのまま並べたもの。 */
function compileEntriesAsResult(entries) {
  return entries
    .map((e) => {
      const staffLabel = e.staff_name ? `（${e.staff_name}）` : '';
      return `［${formatEntryDate(e.entry_date)}］${staffLabel} ${e.content}`.trim();
    })
    .join('\n');
}

function entryParagraphs(entries) {
  if (!entries.length) return [new Paragraph({ children: [new TextRun('（記録なし）')] })];
  return entries.flatMap((e) => {
    const lines = e.content.split('\n').map((l) => l.trim()).filter(Boolean);
    const heading = e.staff_name ? `［${formatEntryDate(e.entry_date)}］対応：${e.staff_name}` : `［${formatEntryDate(e.entry_date)}］`;
    return [
      new Paragraph({ spacing: { before: 100, after: 30 }, children: [new TextRun({ text: heading, bold: true })] }),
      ...(lines.length ? lines.map((line) => new Paragraph({ children: [new TextRun(line)] }))
        : [new Paragraph({ children: [new TextRun('（該当なし）')] })]),
    ];
  });
}

/**
 * ほおっちょけんの相談案件を、法人所定の様式第１号－①②のレイアウトに沿った
 * Wordファイル(Blob)にする。caseData: { title, intake, mitigation, result }
 */
export async function buildHocchokenCaseWordBlob(caseData, entries) {
  const { title, intake = {}, mitigation, result } = caseData;
  const resultText = result?.trim() || compileEntriesAsResult(entries);

  const doc = new Document({
    styles: {
      default: {
        document: { run: { font: BODY_FONT, size: 24 } },
      },
    },
    sections: [{
      properties: {
        // 明示しないとdocxの既定余白(上下左右1440DXA=1インチ)になり、印刷可能幅が
        // 9026DXAしかなく表の幅(9100DXA)をわずかに下回って右側がはみ出す。
        // A4・実際の様式に合わせた余白にして印刷可能幅を10091DXAまで広げる。
        page: {
          size: { width: 11906, height: 16838 }, // A4
          margin: { top: 720, right: 851, bottom: 720, left: 964, header: 851, footer: 992, gutter: 0 },
        },
      },
      children: [
        formHeaderLine('様式第１号－①'),
        new Paragraph({
          alignment: AlignmentType.RIGHT,
          spacing: { before: 40, after: 120 },
          children: [new TextRun({ text: '（困りごと受付者記入用）', size: 18 })],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 60 },
          children: [new TextRun({ text: '困りごと相談　受付　・　経過記録書', bold: true, size: 28 })],
        }),
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 },
          children: [new TextRun({ text: title, bold: true })],
        }),

        receptionTable(intake),
        new Paragraph({ text: '' }),

        new Paragraph({
          spacing: { before: 120, after: 80 },
          children: [new TextRun({ text: '□困りごとを持つ人（地域住民・入所者　・利用者等の当事者）', bold: true })],
        }),
        personTable(intake),
        new Paragraph({ text: '' }),

        new Paragraph({
          spacing: { before: 120, after: 80 },
          children: [new TextRun({ text: '□困りごとの申出人（『当事者との関係』欄で『1.本人』以外の場合、記入する）', bold: true })],
        }),
        reporterTable(intake),
        new Paragraph({ text: '' }),

        issueTable(intake),
        new Paragraph({ text: '' }),

        formHeaderLine('様式第１号－②'),
        new Paragraph({ text: '', spacing: { after: 120 } }),
        boxedSection('対応経過', entryParagraphs(entries)),
        new Paragraph({ text: '' }),
        boxedSection('軽減・解決策', mitigation ? [new Paragraph({ children: [new TextRun(mitigation)] })] : [new Paragraph({ children: [new TextRun('')] })]),
        new Paragraph({ text: '' }),
        boxedSection('結　　果', resultText ? resultText.split('\n').map((l) => new Paragraph({ children: [new TextRun(l)] })) : [new Paragraph({ children: [new TextRun('')] })]),
      ],
    }],
  });
  return Packer.toBlob(doc);
}
